// Copyright (c) StrangeBee 2023

import { useParams } from 'react-router-dom';
import { useListAllFields } from '@Queries/describeQueries';
import { AttachmentCasesList } from './Cases/AttachmentCasesList';
import { AttachmentTasksList } from './Tasks/AttachmentTasksList';
import { AttachmentReportsList } from './Reports/AttachmentReportsList';
import { useMemo } from 'react';
import { THPreview, usePreview } from '@Molecules/THPreview';
import useOneQuery from '@Hooks/useOneQuery';
import { CaseOutput } from 'thehive-sdk';
import { computeGetSelector } from '@Organisms/Case/selector';
import client from '@Client';

type AttachmentListPanelProps = {
    hasPermission: boolean;
};

export const AttachmentListPanelContent = ({ hasPermission }: AttachmentListPanelProps) => {
    const { caseId, sectionId = 'cases' } = useParams<{ caseId: string; sectionId: 'cases' | 'tasks' | 'reports' }>();
    const { data: describe } = useListAllFields();
    const previewManager = usePreview();

    const { data: caseData } = useOneQuery<CaseOutput>({
        selector: computeGetSelector({ caseId }),
        controller: client.CaseController,
        manual: true,
        fetchOnStart: false,
    });

    const ListBlock = useMemo(() => {
        switch (sectionId) {
            case 'reports': {
                return (
                    <AttachmentReportsList
                        describe={describe}
                        parentId={caseId}
                        parentNumber={caseData?.number}
                        previewManager={previewManager}
                        hasPermissions={hasPermission}
                    />
                );
            }

            case 'tasks': {
                return (
                    <AttachmentTasksList
                        describe={describe}
                        parentId={caseId}
                        parentNumber={caseData?.number}
                        previewManager={previewManager}
                    />
                );
            }

            case 'cases':
            default: {
                return (
                    <AttachmentCasesList
                        describe={describe}
                        parentId={caseId}
                        parentNumber={caseData?.number}
                        previewManager={previewManager}
                        hasPermissions={hasPermission}
                    />
                );
            }
        }
    }, [caseId, describe, hasPermission, previewManager, sectionId, caseData?.number]);

    return (
        <>
            {/* Preview */}
            {previewManager.content && previewManager.type && (
                <THPreview
                    src={previewManager.content}
                    onClick={previewManager.togglePreview}
                    type={previewManager.type}
                    extension={previewManager.extension}
                />
            )}
            {/* List */}
            {ListBlock}
        </>
    );
};
